import React from "react";

/**
 * SilhouetteVehicle — fallback premium WYVIO.
 * Ce composant s'affiche UNIQUEMENT quand aucune photo réelle n'est disponible
 * et qu'aucune image constructeur n'a pu être récupérée automatiquement.
 * Il doit rester haut de gamme même sans photo.
 *
 * Types : "berline" | "suv" | "coupe" | "break".
 * L'IA de WYVIO sélectionnera automatiquement la meilleure photo réelle
 * pour remplacer cette silhouette dès qu'un cliché extérieur est ajouté.
 */
const PATHS = {
  berline:
    "M20 130 L45 90 Q60 78 90 76 L200 76 Q235 78 260 90 L295 105 Q315 108 320 118 L322 138 Q322 148 310 148 L295 148 Q292 158 280 162 Q268 166 258 162 Q248 158 246 148 L94 148 Q90 158 78 162 Q66 166 56 162 Q46 158 44 148 L28 148 Q18 148 18 138 Z",
  suv:
    "M18 132 L35 78 Q46 60 80 58 L215 58 Q248 60 268 78 L305 108 Q320 112 322 122 L324 140 Q324 152 312 152 L298 152 Q294 162 282 166 Q270 170 260 166 Q250 162 248 152 L96 152 Q92 162 80 166 Q68 170 58 166 Q48 162 46 152 L28 152 Q16 152 16 140 Z",
  coupe:
    "M22 130 L58 96 Q78 82 118 80 L200 80 Q246 84 272 100 L308 118 Q322 122 324 132 L326 140 Q326 148 314 148 L298 148 Q294 158 282 162 Q270 166 260 162 Q250 158 248 148 L94 148 Q90 158 78 162 Q66 166 56 162 Q46 158 44 148 L30 148 Q20 148 20 140 Z",
  break:
    "M18 130 L38 82 Q52 70 82 68 L230 68 Q254 72 268 88 L302 118 Q318 122 320 132 L322 140 Q322 150 310 150 L296 150 Q292 160 280 164 Q268 168 258 164 Q248 160 246 150 L94 150 Q90 160 78 164 Q66 168 56 164 Q46 160 44 150 L28 150 Q18 150 18 140 Z",
};

const WINDOWS = {
  berline: "M90 92 L108 82 L200 82 Q220 82 232 90 L255 108 L90 108 Z",
  suv: "M92 82 L110 66 L215 66 Q240 66 252 82 L268 106 L92 106 Z",
  coupe: "M110 96 L134 84 L210 84 Q238 86 254 100 L272 118 L110 118 Z",
  break: "M90 82 L110 74 L230 74 Q246 76 254 88 L268 108 L90 108 Z",
};

/**
 * `hint` : petite mention discrète en bas ("Photos en attente").
 * Passe `hint={false}` pour retirer l'indication.
 */
export function SilhouetteVehicle({
  type = "berline",
  className = "",
  testid,
  hint = false,
  variant = "light", // "light" (fond clair) | "dark" (fond sombre, hero)
}) {
  const path = PATHS[type] || PATHS.berline;
  const win = WINDOWS[type] || WINDOWS.berline;
  const isDark = variant === "dark";
  const stroke = isDark ? "rgba(255,255,255,0.28)" : "#C4C4B8";
  const strokeSoft = isDark ? "rgba(255,255,255,0.14)" : "#D9D9D2";
  const fillWin = isDark ? "rgba(255,255,255,0.04)" : "rgba(21,21,21,0.03)";
  const watermark = isDark ? "rgba(255,255,255,0.20)" : "#B8B8B0";
  const bgClass = isDark ? "" : "wyvio-silhouette-bg";

  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden ${bgClass} ${className}`}
      style={
        isDark
          ? {
              background:
                "radial-gradient(ellipse at 50% 40%, rgba(255,255,255,0.06) 0%, rgba(21,21,21,0) 55%), #151515",
            }
          : undefined
      }
    >
      {/* Ligne horizon très subtile */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-8 h-px opacity-50"
        style={{
          background: isDark
            ? "linear-gradient(to right, transparent, rgba(255,255,255,0.14), transparent)"
            : "linear-gradient(to right, transparent, #C4C4B8, transparent)",
        }}
      />

      <svg
        viewBox="0 0 340 180"
        className="relative h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Reflet vitrage subtil */}
        <path d={win} fill={fillWin} />

        {/* Contour véhicule */}
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />

        {/* Roues */}
        <circle cx="70" cy="152" r="14" fill="none" stroke={stroke} strokeWidth="1.5" />
        <circle cx="272" cy="152" r="14" fill="none" stroke={stroke} strokeWidth="1.5" />
        <circle cx="70" cy="152" r="6" fill="none" stroke={strokeSoft} strokeWidth="1" />
        <circle cx="272" cy="152" r="6" fill="none" stroke={strokeSoft} strokeWidth="1" />
      </svg>

      {/* Watermark discret */}
      <span
        className="absolute bottom-3 right-4 text-[9px] uppercase tracking-[0.24em]"
        style={{ color: watermark }}
      >
        wyvio
      </span>

      {/* Indication "photos en attente" — désactivée par défaut */}
      {hint && (
        <span
          className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9.5px] font-medium uppercase tracking-[0.16em]"
          style={{
            background: isDark ? "rgba(255,255,255,0.08)" : "rgba(21,21,21,0.05)",
            color: isDark ? "rgba(255,255,255,0.72)" : "#6B6B67",
          }}
        >
          <span
            aria-hidden
            className="inline-block h-1 w-1 rounded-full"
            style={{ background: isDark ? "rgba(255,255,255,0.5)" : "#8A8A80" }}
          />
          Photos en attente
        </span>
      )}
    </div>
  );
}
